'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@chakra-ui/react';
import { Montserrat, Roboto } from 'next/font/google';
import styles from './triple-content.module.scss';

interface TripleContentProps {
  topTitle?: string;
  title?: string;
  content?: string;
  blue?: boolean;
  dark?: boolean;
  white?: boolean;
  green?: boolean;
  fadeIn?: boolean;
  deskCenter?: boolean;
  ariaLive?: boolean;
}

const Montserrat600 = Montserrat({ subsets: ['latin'], weight: ['600'] });
const Roboto400 = Roboto({ subsets: ['latin'], weight: ['400'] });

const TripleContent: React.FC<TripleContentProps> = ({
  topTitle,
  title,
  content,
  blue,
  dark,
  white,
  green,
  fadeIn,
  deskCenter,
  ariaLive,
}) => {
  const [isVisible, setVisible] = useState(false);
  const domRef = useRef<HTMLDivElement>(null);
  const HeadingTag = topTitle ? 'h3' : 'h2';

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        setVisible(entry.isIntersecting);
      });
    });
    observer.observe(domRef.current as Element);
  }, []);

  return (
    <Box
      ref={domRef}
      role="listitem"
      className={`${dark ? styles.dark : ''} ${fadeIn ? 'fade-in-section' : ''} ${isVisible ? 'is-visible' : ''} ${
        green ? styles.green : ''
      } ${deskCenter ? styles.desk_center : ''}`}
    >
      {topTitle ? (
        <h2
          className={`${Roboto400.className} ${styles.top_title} ${blue ? styles.blue : white ? styles.white : ''}`}
        >
          {topTitle}
        </h2>
      ) : (
        ''
      )}
      {title ? (
        <HeadingTag
          className={`${Montserrat600.className} ${styles.title}`}
          aria-live={ariaLive ? 'polite' : undefined}
        >
          {title}
        </HeadingTag>
      ) : (
        ''
      )}
      {content ? <p className={`${Roboto400.className} ${styles.content}`}>{content}</p> : ''}
    </Box>
  );
};

export default TripleContent;
